import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface FaqItem {
  question: string;
  answer: string;
  open: boolean;
}

interface ReviewItem {
  quote: string;
  initial: string;
  name: string;
  context: string;
}

interface ScheduleRow {
  date: string;
  type: string;
  time: string;
  venue: string;
  price: string;
  bookUrl: string;
}

interface CompareRow {
  label: string;
  typical: boolean;
}

@Component({
  selector: 'app-chelmsford-mock',
  imports: [CommonModule, RouterModule],
  templateUrl: './chelmsford-mock.component.html',
  styleUrl: './chelmsford-mock.component.css',
})
export class ChelmsfordMockComponent {
  
  // Used to render 5-star ratings via *ngFor
  stars: number[] = [1, 2, 3, 4, 5];

  // Only two CSSE mocks are currently open for booking, each with its own booking link
  scheduleRows: ScheduleRow[] = [
    {
      date: 'Sat, 29 Aug 2026',
      type: 'CSSE',
      time: '10:00 AM – 12:30 PM',
      venue: 'Chelmsford SJP School, CM1 4BS',
      price: '£30',
      bookUrl: 'https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/23923f483c337cd3ea599498cb7338c3'
    },
    {
      date: 'Sat, 12 Sep 2026',
      type: 'CSSE',
      time: '10:00 AM – 12:30 PM',
      venue: 'Chelmsford SJP School, CM1 4BS',
      price: '£30',
      bookUrl: 'https://www.sehyog.co.uk/11plus-help-club/category/11-mock-test/950193abfc29b42b4739e8b065ab2ee6'
    }
  ];

  faqs: FaqItem[] = [
    {
      question: 'What happens on the day?',
      answer: 'Your child sits a full-length, exam-board-style paper under real timed exam conditions at our Chelmsford centre, supervised by invigilators, just as they will on the real 11+ day.',
      open: false
    },
    {
      question: 'When are results released?',
      answer: 'Full results, cohort ranking and the detailed performance report are released within a few working days of the mock.',
      open: false
    },
    {
      question: 'Can we take the paper home?',
      answer: 'Yes. Every SEHYOG mock includes a take-home copy of the question paper along with full worked solutions.',
      open: false
    },
    {
      question: 'How is ranking calculated?',
      answer: 'Your child\'s raw score is converted to a rank and percentile against every other student who sat that mock cohort, giving a realistic sense of where they stand.',
      open: false
    },
    {
      question: 'Which mock should I book?',
      answer: 'Book the CSSE mock if applying to KEGS, CRGS, Westcliff, Southend or other Essex CSSE consortium schools. Book the CCHS/FSCE mock if applying to Chelmsford County High School for Girls.',
      open: false
    },
    {
      question: 'What\'s the difference between CSSE and FSCE?',
      answer: 'CSSE (Consortium of Selective Schools in Essex) sets the shared entrance test used by KEGS, CRGS, Westcliff, Southend and other grammar schools. FSCE is the separate format used specifically for Chelmsford County High School for Girls (CCHS).',
      open: false
    }
  ];

  reviews: ReviewItem[] = [
    {
      quote: 'The detailed analysis showed us exactly which areas our daughter needed to work on before the CCHS exam. It changed how we prepped for the final month.',
      initial: 'R',
      name: 'Rebecca M.',
      context: 'Parent, CCHS applicant'
    },
    {
      quote: 'Exam conditions felt completely real. The cohort ranking finally gave us a genuine sense of where our son stood ahead of KEGS.',
      initial: 'A',
      name: 'Amit P.',
      context: 'Parent, KEGS applicant'
    },
    {
      quote: 'The take-home paper and worked solutions were brilliant — we could sit down together and go through every mistake.',
      initial: 'S',
      name: 'Sarah K.',
      context: 'Parent, CRGS applicant'
    }
  ];

  compareRows: CompareRow[] = [
    { label: 'Full exam conditions', typical: true },
    { label: 'Original exam-style paper', typical: true },
    { label: 'Score', typical: true },
    { label: 'Cohort rank', typical: false },
    { label: 'Percentile', typical: false },
    { label: 'Detailed analysis', typical: false },
    { label: 'Take-home paper', typical: false },
    { label: 'Solutions', typical: false },
    { label: 'Post-mock support', typical: false }
  ];

  ngOnInit(): void {
    this.loadFonts();
    this.loadIcons();
    this.loadStructuredData();
  }

  toggleFaq(index: number): void {
    var self = this;
    self.faqs.forEach(function (item: FaqItem, i: number) {
      if (i !== index) {
        item.open = false;
      }
    });
    self.faqs[index].open = !self.faqs[index].open;
  }

  scrollToReport(event: Event): void {
    event.preventDefault();
    var target = document.getElementById('ce-report');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private loadFonts(): void {
    if (document.getElementById('ce-font-link')) {
      return;
    }
    var link = document.createElement('link');
    link.id = 'ce-font-link';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;600;700&display=swap';
    document.head.appendChild(link);
  }

  private loadIcons(): void {
    if (document.getElementById('ce-icon-link')) {
      return;
    }
    var link = document.createElement('link');
    link.id = 'ce-icon-link';
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css';
    link.onerror = function () {
      if (document.getElementById('ce-icon-link-fallback')) {
        return;
      }
      var fallback = document.createElement('link');
      fallback.id = 'ce-icon-link-fallback';
      fallback.rel = 'stylesheet';
      fallback.href = 'https://cdn.jsdelivr.net/npm/@fortawesome/fontawesome-free@6.5.2/css/all.min.css';
      document.head.appendChild(fallback);
    };
    document.head.appendChild(link);
  }

  private loadStructuredData(): void {
    if (!document.getElementById('ce-org-schema')) {
      var orgScript = document.createElement('script');
      orgScript.type = 'application/ld+json';
      orgScript.id = 'ce-org-schema';
      orgScript.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'EducationalOrganization',
        'name': 'SEHYOG Academy',
        'url': 'https://sehyog.co.uk',
        'areaServed': 'Chelmsford, Essex',
        'description': '11+ mock exams and tuition for CSSE and CCHS/FSCE selective school entrance in Chelmsford and across Essex.',
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.9',
          'reviewCount': '214'
        }
      });
      document.head.appendChild(orgScript);
    }

    if (!document.getElementById('ce-faq-schema')) {
      var faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.id = 'ce-faq-schema';
      faqScript.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': this.faqs.map(function (f: FaqItem) {
          return {
            '@type': 'Question',
            'name': f.question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': f.answer
            }
          };
        })
      });
      document.head.appendChild(faqScript);
    }
  }
}
